import { UserClaims } from '@okta/okta-auth-js';
import {
  CommentResponse,
  CreateCommentInternalInput,
  EditCommentInputInternal,
  EditCommentPayload,
  QueryCoralcommentsArgs,
  ReplyCommentMutationVariables,
} from '@repo/api/open-calls/graphql';
import request from 'graphql-request';
import { ConfigService } from '../../services';
import { QueryStateManagement } from '../../state';
import * as constants from '../constants';
import {
  mutationCreateComment,
  mutationEditComment,
  mutationReplyComment,
  queryGetComment,
  queryGetComments,
} from './queries.graphql';

export type QueryCoralcommentsInternalArgs = Omit<
  QueryCoralcommentsArgs,
  'storyID'
>;

export class CommentsService {
  static async createComment(variables: {
    input: CreateCommentInternalInput;
    user: UserClaims;
  }): Promise<CommentResponse> {
    try {
      const response = await QueryStateManagement.client.fetchQuery({
        queryKey: ['create-comment', variables.input.submissionID],
        queryFn: () =>
          request<{
            createComment: { edge: { node: Record<string, unknown> } };
          }>(
            `${ConfigService.BACKEND_GRAPHQL_ENDPOINT}${window.location.search}`,
            mutationCreateComment,
            variables,
            {
              Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
            },
          ),
        gcTime: constants.QUERY_CACHE_TIME,
      });
      const coralComment = response.createComment.edge.node;
      return {
        id: coralComment.id,
        createdAt: coralComment.createdAt,
        childCount: coralComment.replyCount,
        revisions: [
          {
            body: coralComment.body ?? variables.input.body,
            meta: {
              createdBy: {
                email: variables.user.email,
                id: variables.user.sub,
                name: variables.user.name,
                username: variables.user.username,
              },
            },
            id: coralComment.id,
            isAnonymous: false,
          },
        ],
      } as CommentResponse;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  }

  static async fetchComments(
    variables: QueryCoralcommentsInternalArgs,
  ): Promise<CommentResponse[]> {
    try {
      const response = await QueryStateManagement.client.fetchQuery({
        queryKey: ['get-comments', variables.submissionID],
        queryFn: () =>
          request<{ coralcomments: CommentResponse[] }>(
            `${ConfigService.BACKEND_GRAPHQL_ENDPOINT}${window.location.search}`,
            queryGetComments,
            variables,
            {
              Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
            },
          ),
        gcTime: constants.QUERY_CACHE_TIME,
      });
      return response.coralcomments || [];
    } catch (error) {
      console.error('Error getting comments:', error);
      throw error;
    }
  }

  static async fetchComment(variables: {
    id: string;
  }): Promise<CommentResponse> {
    try {
      const response = await QueryStateManagement.client.fetchQuery({
        queryKey: ['get-comment', variables.id],
        queryFn: () =>
          request<{ coralcomment: CommentResponse }>(
            `${ConfigService.BACKEND_GRAPHQL_ENDPOINT}${window.location.search}`,
            queryGetComment,
            variables,
            {
              Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
            },
          ),
        gcTime: constants.QUERY_CACHE_TIME,
      });
      return response.coralcomment;
    } catch (error) {
      console.error('Error getting comment:', error);
      throw error;
    }
  }

  static async updateComment(variables: {
    input: EditCommentInputInternal;
  }): Promise<EditCommentPayload> {
    try {
      const response = await QueryStateManagement.client.fetchQuery({
        queryKey: ['update-comment', variables.input.commentID],
        queryFn: () =>
          request<{ editComment: EditCommentPayload }>(
            `${ConfigService.BACKEND_GRAPHQL_ENDPOINT}`,
            mutationEditComment,
            variables,
            {
              Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
            },
          ),
        gcTime: constants.QUERY_CACHE_TIME,
      });
      return response.editComment;
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  }

  static async replyComment(
    variables: ReplyCommentMutationVariables,
  ): Promise<CommentResponse> {
    try {
      const response = await QueryStateManagement.client.fetchQuery({
        queryKey: ['reply-comment', variables.input.parentID],
        queryFn: () =>
          request<{ createCommentReply: CommentResponse }>(
            `${ConfigService.BACKEND_GRAPHQL_ENDPOINT}`,
            mutationReplyComment,
            variables,
            {
              Authorization: `Bearer ${sessionStorage.getItem(constants.TOKEN_STORAGE_KEY)}`,
            },
          ),
        gcTime: constants.QUERY_CACHE_TIME,
      });
      return response.createCommentReply;
    } catch (error) {
      console.error('Error replying to comment:', error);
      throw error;
    }
  }
}
